/**
 * @file src/_modulos/login/visuais/form/LoginFormFields.tsx
 * @description O `LoginFormFields` é um componente de formulário React que gerencia a entrada de credenciais de usuário (nome de usuário e senha)
 * e lida com o processo de autenticação. Ele integra `react-hook-form` para validação e gerenciamento de estado do formulário,
 * além de `next/navigation` para roteamento pós-login e `react-hot-toast` para feedback visual ao usuário.
 */

"use client";
import { useForm } from "react-hook-form"; // Hook para gerenciamento de formulários.
import { LogIn } from "lucide-react"; // Ícone de login.
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  PasswordInput,
  Button,
} from "visuais"; // Componentes de formulário e botão da biblioteca `visuais`.
import { checkPasswordExpiration, useLoginFlow } from "@login"; // Hook customizado para a lógica de fluxo de login.
import { useRouter, useSearchParams } from "next/navigation"; // Hooks do Next.js para roteamento e parâmetros de URL.
import toast from "react-hot-toast"; // Biblioteca para exibir notificações toast.
import { JSX } from "react";

/**
 * @typedef {Object} LoginFormFieldsType
 * @description Define os tipos de campos esperados no formulário de login.
 */
type LoginFormFieldsType = {
  /**
   * @property {string} username - O nome de usuário para login.
   */
  username: string;
  /**
   * @property {string} password - A senha do usuário para login.
   */
  password: string;
};

/**
 * @function LoginFormFields
 * @description Componente React que renderiza os campos de entrada de usuário e senha para um formulário de login.
 * Ele gerencia o estado do formulário, validações e a submissão, interagindo com o fluxo de login
 * e fornecendo feedback ao usuário através de notificações. Após um login bem-sucedido, redireciona o usuário.
 * @returns {JSX.Element} Os campos do formulário de login e o botão de submissão.
 * @example
 * ```tsx
 * <LoginFormFields />
 * ```
 */
export function LoginFormFields(): JSX.Element {
  const form = useForm<LoginFormFieldsType>({
    defaultValues: { username: "", password: "" },
    mode: "onChange",
  });
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectPath = searchParams.get("redirect") ?? "/";
  const { loginAll, loading } = useLoginFlow({
    username: form.watch("username"),
  });

  const onSubmit = form.handleSubmit(async (data) => {
    await toast.promise(
      new Promise(async (resolve, reject) => {
        try {
          const result = await loginAll({
            username: data.username,
            password: data.password,
          });

          if (result) {
            const lastChange = result.acessoData.usuario.ultimaAlteracao
            if (lastChange) {
              const remainingDays = checkPasswordExpiration(result.acessoData.usuario.ultimaAlteracao);
              
              if (remainingDays <= 7) {
                toast(
                  `⚠️ Sua senha expira em ${remainingDays} dia${
                    remainingDays > 1 ? "s" : ""
                  }. Redefina em breve no Protheus.`
                );
              } 
            }

            resolve("Login realizado com sucesso!");
            router.push(redirectPath);
          } else {
            console.error(
              "❌ [LoginFormFields] Login falhou - resultado null/undefined"
            );
            reject(new Error("Usuário ou senha inválidos"));
          }
        } catch (error) {
          console.error("💥 [LoginFormFields] Erro durante login:", error);
          reject(error);
        }
      }),
      {
        loading: "Entrando...",
        success: (msg) => String(),
        error: (err) => err?.message || "Erro ao fazer login",
      }
    );
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-4">
        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Usuário</FormLabel>
              <FormControl>
                <Input
                  className="text-mono font-white"
                  placeholder="nome.sobrenome"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Senha</FormLabel>
              <FormControl>
                <PasswordInput placeholder="••••••••" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="relative w-full gap-3 text-white shadow-lg transition-all duration-500 ease-in-out bg-gradient-to-r from-cyan-500 to-sky-600 bg-[length:200%_100%] bg-[position:0%_0%] hover:bg-[position:100%_0%]"
          disabled={loading}
        >
          <LogIn className="mr-2 h-4 w-4" />
          Entrar
        </Button>
      </form>
    </Form>
  );
}
